"use client";

import React, { useState, useEffect, type FC } from "react";
import MemeSwapCard from "./MemeSwapCard";
import { useWallet } from "@/stores/wallet.store";
import { useMemeSwapListStore } from "@/stores/meme-swap-list.store";
import { useAssets } from "@/stores/assets.store";
import { cn } from "@/lib/utils";
import ErrorBoundary from "./ErrorBoundary";

// Define the expected status types, matching the swap component's prop type
type SwapStatus = "Active" | "Cancelled" | "In Progress" | "Success" | "Failed" | "Rejected" | "Migrated/Unknown";

import { useMemeSwapListPolling } from "@/stores/meme-swap-list.store";

const SwapList: FC<{ twoCol?: boolean; showHeader?: boolean; showMemes?: boolean; className?: string }> = ({
    twoCol = false,
    className,
}) => {
    // --- STATE ---
    const [sellAmounts, setSellAmounts] = useState<Record<string, string>>({});
    const [revealedSwaps, setRevealedSwaps] = useState(false);
    const [hasNewSwaps, setHasNewSwaps] = useState(false);
    const [prevListLength, setPrevListLength] = useState(0);

    // --- STORES ---
    const memeSwapList = useMemeSwapListStore((s) => s.memeSwapList);
    const error = useMemeSwapListStore((s) => s.error);
    const assets = useAssets((s) => s.assets);
    const { externalWallet, currentWallet } = useWallet();
    const isLoading = useMemeSwapListStore((s) => s.isLoading);
    useMemeSwapListPolling(10000);

    // --- CONSTANTS & TYPES ---
    const swapStatusMap: { [key: number]: SwapStatus } = {
        0: "Active",
        1: "Cancelled",
        2: "In Progress",
        3: "Success",
        4: "Failed",
        5: "Rejected",
        6: "Migrated/Unknown",
    };
    const isWalletNotFoundError = typeof error === 'string' && error.includes("Wallet not found");
    const assetsLoaded = assets && Array.isArray(assets) && assets.length > 0;

    // --- HELPERS ---
    const getAssetByGuid = (guid?: string) => assets.find((a) => a.guid === guid);

    const mapBackendSwap = (swap: any) => {
        const assetIn = getAssetByGuid(swap.assetInGuid);
        const assetOut = getAssetByGuid(swap.assetOutGuid);
        return {
            guid: swap.guid,
            assetInInfo: {
                guid: assetIn?.guid || swap.assetInGuid || "unknown-guid",
                name: assetIn?.name || swap.assetInGuid || "Unknown",
                symbol: assetIn?.symbol || "???",
                image: assetIn?.image,
                jettonMasterAddress: assetIn?.jettonMasterAddress || swap.assetInGuid || "",
                isVerified: assetIn?.isVerified || false,
            },
            assetOutInfo: {
                guid: assetOut?.guid || swap.assetOutGuid || "unknown-guid",
                name: assetOut?.name || swap.assetOutGuid || "Unknown",
                symbol: assetOut?.symbol || "???",
                image: assetOut?.image,
                jettonMasterAddress: assetOut?.jettonMasterAddress || swap.assetOutGuid || "",
                isVerified: assetOut?.isVerified || false,
            },
            assetInAmount: swap.assetInAmount?.toString() ?? "0",
            status: swap.status,
            walletAddress: swap.walletAddress ?? undefined,
            watchAddress: swap.watchAddress ?? undefined,
            revealed: true,
        };
    };

    // --- MEMOIZED COMPUTED LIST ---
    const displayedSwapList = React.useMemo(() => {
        if (!memeSwapList) return [];
        return memeSwapList
            .map(mapBackendSwap)
            .filter(
                (swap) =>
                    swap &&
                    swap.guid &&
                    swap.assetInInfo &&
                    swap.assetInInfo.guid &&
                    swap.assetOutInfo &&
                    swap.assetOutInfo.guid,
            );
    }, [memeSwapList, assets]);

    // --- HANDLERS ---
    const handleSetSellAmount = React.useCallback((swapId: string, value: string) => {
        setSellAmounts((prev) => ({ ...prev, [swapId]: value }));
    }, []);


    // --- EFFECTS ---
    // Reset all local state when wallet changes
    useEffect(() => {
        setSellAmounts({});
        setRevealedSwaps(false);
        setHasNewSwaps(false);
        setPrevListLength(0);
    }, [currentWallet?.guid]);

    // Track new swaps for animation
    useEffect(() => {
        if (displayedSwapList.length !== prevListLength) {
            setPrevListLength(displayedSwapList.length);
            if (displayedSwapList.length > 0) {
                setHasNewSwaps(true);
                setRevealedSwaps(false);
            }
        }
    }, [displayedSwapList.length, prevListLength]);

    // Animate new swaps
    useEffect(() => {
        if (displayedSwapList.length > 0 && !revealedSwaps && hasNewSwaps) {
            const timeoutId = setTimeout(() => {
                setRevealedSwaps(true);
                setHasNewSwaps(false);
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [hasNewSwaps, revealedSwaps, displayedSwapList.length]);

    // --- EARLY RETURN HANDLER ---
    function getEarlyReturnElement() {
        if (!currentWallet || !externalWallet || !assetsLoaded) {
            return (
                <div className="card text-center text-gray-400 p-4 col-span-2 h-full items-center justify-center flex">
                    <span className="max-w-80">Loading wallet information...</span>
                </div>
            );
        }
        if (!currentWallet?.guid) {
            return (
                <div className="card text-center text-gray-400 p-4 col-span-2 h-full items-center justify-center flex">
                    <span className="max-w-80">Please connect your wallet to view MemeSwaps.</span>
                </div>
            );
        }
        if (isWalletNotFoundError) {
            return (
                <div className="card text-center text-gray-400 p-4 col-span-2 h-full items-center justify-center flex">
                    <span className="max-w-80">
                        This wallet is not registered with the MemeSwap service. Please use a Nest wallet.
                    </span>
                </div>
            );
        }
        if (isLoading && memeSwapList && memeSwapList.length === 0) {
            return (
                <div className="card text-center text-gray-400 p-4 col-span-2 h-full items-center justify-center flex">
                    <span className="max-w-80">Loading MemeSwaps...</span>
                </div>
            );
        }
        if (error) {
            return (
                <div className="card text-center text-red-400 p-4 col-span-2 h-full items-center justify-center flex">
                    <span className="max-w-80">Error: {error}</span>
                </div>
            );
        }
        if (!displayedSwapList || displayedSwapList.length === 0) {
            const isExternal =
                currentWallet &&
                (currentWallet.guid === externalWallet.guid ||
                    currentWallet.friendlyAddress === externalWallet.friendlyAddress);
            return (
                <div className="card text-center text-gray-400 p-4 col-span-2 h-full flex items-center justify-center">
                    <span className="max-w-80">
                        {isExternal ? "MemeSwaps are available only for Nest wallets." : "No active memeSwaps found."}
                    </span>
                </div>
            );
        }
        return null;
    }

    const earlyReturnElement = getEarlyReturnElement();
    if (earlyReturnElement) return earlyReturnElement;


    return (
        <section
            className={cn(
                "gap-4 overflow-y-auto md:min-h-full md:h-auto w-full !scrollbar-hide max-w-full relative items-start",
                twoCol ? "grid md:grid-cols-2 grid-cols-1 gap-4 auto-rows-auto" : "flex flex-col items-center",
                className,
            )}
        >
            {/* ErrorBoundary prevents the whole page from crashing if a card throws */}
            <ErrorBoundary
                fallback={
                    <div className="card text-center text-red-400 p-4 col-span-2 h-full items-center justify-center flex">
                        <span className="max-w-80">
                            A rendering error occurred. Please try switching wallets again or reload the page.
                        </span>
                    </div>
                }
            >
                {displayedSwapList.length === 0 && (
                    <div className="text-center text-gray-400 p-4">No swaps to show for this wallet.</div>
                )}
                {displayedSwapList.map((swap: any) => {
                    // Defensive: skip if swap or asset info is missing
                    if (!swap || !swap.guid || !swap.assetOutInfo || !swap.assetInInfo) return null;
                    const profitPercentage = 0;
                    const age = "N/A";
                    const usdPrice = 0;
                    const currentSellAmount = sellAmounts[swap.guid] || "";
                    const status = (swapStatusMap[Number(swap.status)] || "Migrated/Unknown") as SwapStatus;

                    return (
                    <MemeSwapCard
                        key={swap.guid}
                        tokenName={swap.assetOutInfo?.name || ""}
                        tokenAddress={swap.assetOutInfo?.jettonMasterAddress || ""}
                        initialAmount={swap.assetInAmount || "0"}
                        profitPercentage={profitPercentage}
                        age={age}
                        sendAmount={currentSellAmount}
                        setSendAmount={(value: string) => handleSetSellAmount(swap.guid, value)}
                        balance={swap.assetInAmount || 0}
                        usdPrice={usdPrice}
                        isSellDisabled={
                            !currentSellAmount ||
                            Number.parseFloat(currentSellAmount) <= 0 ||
                            swap.walletAddress === externalWallet.friendlyAddress ||
                            swap.walletAddress === externalWallet.guid
                        }
                        status={status}
                        assetOutInfo={swap.assetOutInfo}
                        assetInInfo={swap.assetInInfo}
                        revealed={swap.revealed !== false}
                        guid={swap.guid}
                    />
                );
            })}
        </ErrorBoundary>
        <div className="hidden lg:flex flex-col w-full flex-1 h-full justify-end ">
            <div className="min-h-20 h-20 w-full min-w-full sticky bottom-0 bg-gradient-to-t from-background to-transparent"></div>
        </div>
    </section>
);

}

export default SwapList;
